// Dual Monitor Detection and Window Management

import { getHangingProtocolByModalityAndBodyPart } from "./helper";

interface ScreenInfo {
  left: number;
  top: number;
  width: number;
  height: number;
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
  isPrimary?: boolean;
}

interface MonitorConfig {
  hasMultipleMonitors: boolean;
  screens: ScreenInfo[];
  primaryScreen: ScreenInfo;
  secondaryScreen?: ScreenInfo;
}

interface LaunchResult {
  success: boolean;
  viewerWindow: Window | null;
  message: string;
}

class MonitorManager {
  private screens: ScreenInfo[] = [];
  private isExtendedMode = false;
  private primaryScreen: ScreenInfo | null = null;
  private secondaryScreen: ScreenInfo | null = null;

  // --- Viewer buffer-tab queue (VIEWER_BUFFER_TABS) ---
  // When VIEWER_BUFFER_TABS > 1, viewer launches share a fixed pool of named
  // windows (viewer_1..viewer_N). Each slot remembers the study it shows AND the
  // actual window handle, so we can tell which tabs are still open. Slot choice
  // prefers, in order:
  //   1. the slot already showing this study in a still-open tab (reuse it);
  //   2. any slot whose tab is closed / never opened (fill the gap first);
  //   3. otherwise the least-recently-used slot gets recycled.
  // This means closing a viewer frees its slot for the next study instead of
  // blindly recycling a tab the radiologist is still using.
  private viewerSlots: { studyUID: string | null; win: Window | null; lastUsed: number }[] = [];
  private viewerUseTick = 0;

  private getViewerBufferCount(): number {
    const raw = (window as any).ENV_CONFIG?.VIEWER_BUFFER_TABS;
    const n = Math.floor(Number(raw));
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }

  private isViewerSlotAlive(slot: { win: Window | null }): boolean {
    return !!slot.win && !slot.win.closed;
  }

  /**
   * Choose which buffer slot a study should open in.
   * Returns the slot index, or null when buffering is disabled (count <= 1) so
   * the caller keeps its legacy target ('viewer' / '_blank').
   */
  private acquireViewerSlot(studyUID: string): number | null {
    const count = this.getViewerBufferCount();
    if (count <= 1) return null;

    // (Re)size the pool if the configured count changed.
    if (this.viewerSlots.length !== count) {
      this.viewerSlots = Array.from({ length: count }, () => ({
        studyUID: null,
        win: null,
        lastUsed: 0,
      }));
      this.viewerUseTick = 0;
    }

    // 1. Already open in a live tab -> reuse that exact tab.
    const sameAlive = this.viewerSlots.findIndex(
      s => s.studyUID === studyUID && this.isViewerSlotAlive(s)
    );
    if (sameAlive !== -1) return sameAlive;

    // 2. Fill an empty / closed slot before evicting anything still open.
    const free = this.viewerSlots.findIndex(s => !this.isViewerSlotAlive(s));
    if (free !== -1) {
      this.viewerSlots[free].studyUID = studyUID;
      return free;
    }

    // 3. All tabs still open -> recycle the least-recently-used one.
    let lru = 0;
    for (let i = 1; i < this.viewerSlots.length; i++) {
      if (this.viewerSlots[i].lastUsed < this.viewerSlots[lru].lastUsed) lru = i;
    }
    this.viewerSlots[lru].studyUID = studyUID;
    return lru;
  }

  /** Record the opened window handle for a slot and mark it most-recently-used. */
  private recordViewerWindow(slotIndex: number | null, win: Window | null): void {
    if (slotIndex === null) return;
    const slot = this.viewerSlots[slotIndex];
    if (!slot) return;
    if (win) slot.win = win;
    slot.lastUsed = ++this.viewerUseTick;
  }

  async detectScreens(): Promise<MonitorConfig> {
    try {
      // Modern Screen API (Chrome 86+)
      if ('getScreenDetails' in window) {
        const permission = await navigator.permissions.query({ name: 'window-management' as PermissionName });

        if (permission.state !== 'denied') {
          const screenDetails = await (window as any).getScreenDetails();
          this.screens = screenDetails.screens;
          this.isExtendedMode = this.screens.length > 1;
          this.primaryScreen = this.screens.find(s => s.isPrimary) || this.screens[0];
          this.secondaryScreen = this.screens.find(s => !s.isPrimary);

          return this.getConfig();
        }
      }
    } catch (error) {
      console.warn('Modern Screen API not available:', error);
    }

    return this.detectLegacy();
  }

  private detectLegacy(): MonitorConfig {
    const { width, height, availWidth, availHeight } = window.screen;

    this.isExtendedMode = availWidth > width || availHeight > height;
    this.primaryScreen = {
      left: 0, top: 0, width, height,
      availLeft: 0, availTop: 0,
      availWidth: Math.min(availWidth, width),
      availHeight: Math.min(availHeight, height),
      isPrimary: true
    };

    if (this.isExtendedMode) {
      this.secondaryScreen = {
        left: width, top: 0,
        width: availWidth - width, height,
        availLeft: width, availTop: 0,
        availWidth: availWidth - width, availHeight,
        isPrimary: false
      };
    }

    return this.getConfig();
  }

  private getConfig(): MonitorConfig {
    return {
      hasMultipleMonitors: this.isExtendedMode,
      screens: this.isExtendedMode ? [this.primaryScreen!, this.secondaryScreen!] : [this.primaryScreen!],
      primaryScreen: this.primaryScreen!,
      secondaryScreen: this.secondaryScreen
    };
  }

  private getWindowFeatures(screen: ScreenInfo, width?: number, height?: number): string {
    const w = width || screen.availWidth * 0.95;
    const h = height || screen.availHeight * 0.95;
    const left = screen.availLeft + (screen.availWidth - w) / 2;
    const top = screen.availTop + (screen.availHeight - h) / 2;

    return [
      `width=${Math.round(w)}`,
      `height=${Math.round(h)}`,
      `left=${Math.round(left)}`,
      `top=${Math.round(top)}`,
      'resizable=yes,scrollbars=yes,status=yes'
    ].join(',');
  }

  async launchDualMonitorSetup(studyUID: string, orderDetails): Promise<LaunchResult> {
    const { pacs_order={} } = orderDetails;
    const {po_modality, po_body_part, po_diag_desc} = pacs_order;

    try {
      const config = await this.detectScreens();
      // --- Dual Viewer Mode (client-specific) ---
      // When DUAL_VIEWER_MODE is enabled, open the viewer on BOTH monitors.
      // Monitor 1 (primary) gets series 1 & 2 via @fyzks/dualMonitor1 protocol.
      // Monitor 2 (secondary) gets series 3 & 4 via @fyzks/dualMonitor2 protocol.
      // Each window receives a ?screenNumber= param (1 or 2) so the viewer can
      // identify which monitor it is running on.
      if ((window as any).ENV_CONFIG?.DUAL_VIEWER_MODE && config.hasMultipleMonitors) {
        return this.launchDualViewerMode(studyUID);
      }

      let viewerUrl = `/viewer/viewer?StudyInstanceUIDs=${studyUID}`;
      let hpId;
      if (po_modality && po_body_part) {
        hpId = getHangingProtocolByModalityAndBodyPart(po_modality, po_body_part, po_diag_desc)
      }

      if (hpId) {
        viewerUrl += `&hangingProtocolId=${hpId}`;
      }

      // Pick a buffer slot. null when VIEWER_BUFFER_TABS <= 1, in which case the
      // legacy per-branch target is used and nothing is tracked.
      const slotIndex = this.acquireViewerSlot(studyUID);
      const bufferTarget = slotIndex === null ? null : `viewer_${slotIndex + 1}`;

      if (config.hasMultipleMonitors && config.secondaryScreen) {
        const features = this.getWindowFeatures(config.secondaryScreen);
        const viewerWindow = window.open(viewerUrl, bufferTarget ?? 'viewer', features);
        this.recordViewerWindow(slotIndex, viewerWindow);
        return {
          success: true,
          viewerWindow,
          message: 'Viewer opened on secondary monitor'
        };
      }

      // Single monitor: legacy opens a fresh tab ('_blank'); buffered mode reuses
      // the chosen named tab instead.
      const viewerWindow = window.open(viewerUrl, bufferTarget ?? '_blank');
      this.recordViewerWindow(slotIndex, viewerWindow);
      return {
        success: true,
        viewerWindow,
        message: 'Single monitor - opened in new tab'
      };

    } catch (error) {
      const viewerWindow = window.open(`/viewer/viewer?StudyInstanceUIDs=${studyUID}`, '_blank');
      return {
        success: false,
        viewerWindow,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Opens the DICOM viewer on both monitors simultaneously (DUAL_VIEWER_MODE).
   *
   * - Monitor 1 (primary screen):  screenNumber=1, protocol @fyzks/dualMonitor1  → series 1 & 2
   * - Monitor 2 (secondary screen): screenNumber=2, protocol @fyzks/dualMonitor2 → series 3 & 4
   *
   * Falls back to two separate tabs when only one monitor is detected.
   */
  private async launchDualViewerMode(studyUID: string): Promise<LaunchResult> {
    const PROTOCOL_MONITOR_1 = '@fyzks/dualMonitor1';
    const PROTOCOL_MONITOR_2 = '@fyzks/dualMonitor2';

    const monitor1Url =
      `/viewer/viewer?StudyInstanceUIDs=${studyUID}` +
      `&screenNumber=1` +
      `&hangingProtocolId=${encodeURIComponent(PROTOCOL_MONITOR_1)}`;

    const monitor2Url =
      `/viewer/viewer?StudyInstanceUIDs=${studyUID}` +
      `&screenNumber=2` +
      `&hangingProtocolId=${encodeURIComponent(PROTOCOL_MONITOR_2)}`;

    try {
      const config = await this.detectScreens();

      if (config.hasMultipleMonitors && config.primaryScreen && config.secondaryScreen) {
        // Open monitor 1 (primary) and monitor 2 (secondary) simultaneously
        const win1 = window.open(
          monitor1Url,
          'fyzks_viewer_monitor1',
          this.getWindowFeatures(config.primaryScreen)
        );
        const win2 = window.open(
          monitor2Url,
          'fyzks_viewer_monitor2',
          this.getWindowFeatures(config.secondaryScreen)
        );
        return {
          success: true,
          // Return the primary monitor window as the representative handle
          viewerWindow: win1,
          message: 'Dual viewer mode: viewer opened on both monitors',
        };
      }

      // Single-monitor fallback: open both in separate tabs
      window.open(monitor1Url, 'fyzks_viewer_monitor1');
      const win2 = window.open(monitor2Url, 'fyzks_viewer_monitor2');
      return {
        success: true,
        viewerWindow: win2,
        message: 'Dual viewer mode (single monitor fallback): opened two tabs',
      };
    } catch (error) {
      // Last-resort fallback
      const viewerWindow = window.open(monitor1Url, '_blank');
      return {
        success: false,
        viewerWindow,
        message: `Dual viewer mode error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  async getMonitorInfo(): Promise<MonitorConfig & { configuration: string }> {
    const config = await this.detectScreens();
    return {
      ...config,
      configuration: config.hasMultipleMonitors ? 'Extended' : 'Single Monitor'
    };
  }
}

// Utility functions
const monitorManager = new MonitorManager();

export const launchViewerOnDualMonitor = async (studyUID: string, orderDetails = {}): Promise<Window | null> => {
  const result = await monitorManager.launchDualMonitorSetup(studyUID, orderDetails);
  if (!result.success) console.warn(result.message);
  return result.viewerWindow;
};

export const checkMonitorSetup = async (): Promise<MonitorConfig & { configuration: string }> => {
  return await monitorManager.getMonitorInfo();
};

export { MonitorManager };
export type { MonitorConfig, LaunchResult, ScreenInfo };
